// shared/plans.ts

export type PlanType = "basic" | "professional" | "enterprise";

export interface PlanLimits {
  // =====================================================
  // {LÍMITES CUANTITATIVOS - Los que ya tenías}
  // =====================================================
  maxShops: number;
  maxProducts: number;
  maxUsersPerShop: number;

  // =====================================================
  // {CARACTERÍSTICAS DEL PUNTO DE VENTA (POS)}
  // {Nuevo: Agrupamos todas las características del POS}
  // =====================================================
  posFeatures: {
    shiftManagement: boolean; // Apertura/cierre de turno
    pettyCash: boolean; // Caja chica
    blindBox: boolean; // Caja ciega
  };

  // =====================================================
  // {MÓDULOS DE LA APLICACIÓN}
  // {Cada módulo puede tener su propio nivel de acceso}
  // =====================================================
  modules: {
    inventory: "basic" | "advanced"; // Inventario básico vs avanzado
    customers: boolean; // Módulo de clientes completo
    reports: "none" | "basic" | "enterprise"; // Nivel de reportes
    telegram: boolean; // Bot de Telegram (básico)
  };

  // =====================================================
  // {ROLES DE USUARIO DISPONIBLES}
  // {El que ya tenías, se mantiene igual}
  // =====================================================
  userRoles: string[];

  // =====================================================
  // {NIVEL DE SOPORTE}
  // {Nuevo: Para diferenciar el tipo de soporte}
  // =====================================================
  support: {
    level: "basic" | "dedicated" | "premium";
    onboarding: boolean; // Onboarding personalizado
    responseTime?: string; // Tiempo de respuesta (ej: "2h")
  };
}

export const PLANS: Record<PlanType, PlanLimits> = {
  // =====================================================
  // {PLAN BÁSICO}
  // =====================================================
  basic: {
    // Límites
    maxShops: 1,
    maxProducts: 15,
    maxUsersPerShop: 2,

    // POS - TODO desactivado
    posFeatures: {
      shiftManagement: false,
      pettyCash: false,
      blindBox: false,
    },

    // Módulos
    modules: {
      inventory: "basic", // Solo precio, stock, umbral
      customers: false, // Sin clientes
      reports: "none", // Sin reportes
      telegram: false, // Sin Telegram
    },

    // Roles
    userRoles: ["supervisor", "cashier"],

    // Soporte básico
    support: {
      level: "basic",
      onboarding: false,
    },
  },

  // =====================================================
  // {PLAN PROFESIONAL}
  // =====================================================
  professional: {
    // Límites
    maxShops: 3,
    maxProducts: 50,
    maxUsersPerShop: 10,

    // POS - Completo
    posFeatures: {
      shiftManagement: true,
      pettyCash: true,
      blindBox: true,
    },

    // Módulos
    modules: {
      inventory: "advanced", // Inventario avanzado completo
      customers: true, // Clientes con historial y estadísticas
      reports: "basic", // Reportes de ventas/productos/clientes/discrepancias/transferencias
      telegram: true, // Bot básico
    },

    // Roles
    userRoles: ["supervisor", "store_manager", "cashier"],

    // Soporte dedicado
    support: {
      level: "dedicated",
      onboarding: false,
    },
  },

  // =====================================================
  // {PLAN ENTERPRISE}
  // =====================================================
  enterprise: {
    // Límites - Ilimitados
    maxShops: Infinity,
    maxProducts: Infinity,
    maxUsersPerShop: Infinity,

    // POS - Completo
    posFeatures: {
      shiftManagement: true,
      pettyCash: true,
      blindBox: true,
    },

    // Módulos
    modules: {
      inventory: "advanced", // Inventario avanzado completo
      customers: true, // Clientes con historial y estadísticas
      reports: "enterprise", // Todo + consolidados multi-tienda + heatmaps
      telegram: true, // Bot básico
    },

    // Roles
    userRoles: ["supervisor", "store_manager", "cashier", "auditor"],

    // Soporte premium
    support: {
      level: "premium",
      onboarding: true,
      responseTime: "2h",
    },
  },
};

// =====================================================
// {NOMBRES AMIGABLES DE LOS PLANES}
// {Se mantiene igual que antes}
// =====================================================
export const PLAN_NAMES: Record<PlanType, string> = {
  basic: "Básico",
  professional: "Profesional",
  enterprise: "Enterprise",
};

// =====================================================
// {PRECIOS DE LOS PLANES}
// {Se mantiene igual que antes}
// =====================================================
export const PLAN_PRICES: Record<
  PlanType,
  { monthly: number; yearly: number }
> = {
  basic: { monthly: 0, yearly: 0 },
  professional: { monthly: 29.99, yearly: 299.99 },
  enterprise: { monthly: 99.99, yearly: 999.99 },
};
